/* eslint-disable sonarjs/no-duplicate-string -- repeated CSS values in keyframes read better inline */
import { heroui } from '@heroui/theme';
import type { Config } from 'tailwindcss';

export const colors = {
  primary: {
    50: '#F0F6FE',
    100: '#DEEAFD',
    200: '#C4DAFC',
    300: '#9BC4FE',
    400: '#68A1FD',
    500: '#477FFC',
    600: '#305EF3',
    700: '#2649DE',
    800: '#243DB2',
    900: '#243989',
    950: '#1A2553',
  },
  supply: {
    50: '#F7FAF5',
    100: '#EFF7F0',
    200: '#DEF1E8',
    300: '#C2E3D3',
    400: '#8CC8AC',
    500: '#5FA78B',
    600: '#4D8D76',
    700: '#2C5F48',
    800: '#285243',
    900: '#183528',
    950: '#0F2119',
  },
  demand: {
    50: '#FDF8F6',
    100: '#FBF2EF',
    200: '#F7E4DE',
    300: '#F1B6B1',
    400: '#ED8681',
    500: '#E46560',
    600: '#C23C32',
    700: '#A32929',
    800: '#801F1F',
    900: '#521415',
    950: '#300B0B',
  },
  overloop: {
    50: '#FBF6FE',
    100: '#F6EAFD',
    200: '#EED8FC',
    300: '#E0BAF8',
    400: '#CD8EF2',
    500: '#BA62EA',
    600: '#A642DB',
    700: '#9933CC',
    800: '#792C9D',
    900: '#63257E',
    950: '#440F5C',
  },
  secondary: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E7E5E4',
    300: '#D5D3D1',
    400: '#A4A09C',
    500: '#76716D',
    600: '#56534F',
    700: '#43403C',
    800: '#282524',
    900: '#1B1917',
    950: '#0B0A09',
  },
  neutral: {
    50: '#FFFFFF',
    100: '#F7F7F7',
    200: '#E8E8E8',
    300: '#D1D1D1',
    400: '#A4A4A4',
    500: '#808080',
    600: '#666666',
    700: '#404040',
    800: '#2B2B2B',
    900: '#0B0B0B',
    950: '#000000',
    // NOTE: all values above are deprecated, only use white or black from neutral
    white: '#FFFFFF',
    black: '#000000',
  },
  warning: {
    50: '#FFFBEC',
    100: '#FDF3CB',
    200: '#F9EAA9',
    300: '#F9D355',
    400: '#F5BC1E',
    500: '#F09E0F',
    600: '#D37709',
    700: '#AE530D',
    800: '#8C4112',
    900: '#723715',
    950: '#411C07',
  },
  success: {
    50: '#F8FDF6',
    100: '#F1F8ED',
    200: '#DBEED2',
    300: '#C3E0BA',
    400: '#91B97F',
    500: '#5C9550',
    600: '#4A7E40',
    700: '#3B6732',
    800: '#2E5025',
    900: '#243C1A',
    950: '#1A2C11',
  },
  danger: {
    50: '#FFF6F9',
    100: '#FBEFF3',
    200: '#F7DEE8',
    300: '#F2C0D5',
    400: '#E598BD',
    500: '#BD0F58',
    600: '#9C0D4B',
    700: '#8F1941',
    800: '#6C1433',
    900: '#410B1E',
    950: '#2B0713',
  },
} as const;

export const createSortlistHerouiPlugin = (
  primary: typeof colors.primary = colors.primary,
  primaryDefaultShade: keyof typeof colors.primary = 500,
) =>
  heroui({
    defaultTheme: 'light',
    themes: {
      light: {
        layout: {
          hoverOpacity: 1,
          disabledOpacity: 1,
          lineHeight: {
            tiny: '0.75rem',
            small: '1rem',
          },
          fontSize: {
            small: '1rem',
          },
        },
        colors: {
          primary: {
            ...primary,
            foreground: '#FFFFFF',
            DEFAULT: primary[primaryDefaultShade],
          },
          secondary: {
            ...colors.secondary,
            foreground: '#FFFFFF',
            DEFAULT: colors.secondary[500],
          },
          warning: {
            ...colors.warning,
            foreground: '#FFFFFF',
            DEFAULT: colors.warning[500],
          },
          success: {
            ...colors.success,
            foreground: '#FFFFFF',
            DEFAULT: colors.success[500],
          },
          danger: {
            ...colors.danger,
            foreground: '#FFFFFF',
            DEFAULT: colors.danger[500],
          },
        },
      },
    },
  });

/**
 * Sortlist design tokens as a Tailwind preset. Consumers must also register a
 * HeroUI plugin — pass a custom palette to re-theme the components:
 *
 * ```ts
 * import sortlistPreset, { createSortlistHerouiPlugin } from '@sortlist/design-system/tailwind-preset';
 *
 * export default {
 *   presets: [sortlistPreset],
 *   content: [
 *     './src/**\/*.{ts,tsx}',
 *     './node_modules/@sortlist/design-system/dist/**\/*.js',
 *     './node_modules/@heroui/theme/dist/**\/*.{js,mjs}',
 *   ],
 *   plugins: [createSortlistHerouiPlugin(myPrimary, 500)],
 * };
 * ```
 *
 * Fonts resolve through the `--font-sans-serif` / `--font-serif` CSS variables,
 * or can be overridden entirely via `theme.extend.fontFamily`.
 */
export const sortlistPreset: Omit<Partial<Config>, 'content'> = {
  darkMode: 'class',
  theme: {
    extend: {
      colors: {
        neutral: colors.neutral,
        supply: colors.supply,
        demand: colors.demand,
        overloop: colors.overloop,
      },
      boxShadow: {
        '2xs': '0 1px 0 0 rgba(0, 0, 0, 0.05)',
        xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        sm: '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10)',
        md: '0 4px 6px 0 rgba(0, 0, 0, 0.10), 0 2px 4px -2px rgba(0, 0, 0, 0.10)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10)',
        xl: '0 20px 25px -5px rgba(0, 0, 0, 0.10), 0 8px 10px 0 rgba(0, 0, 0, 0.10)',
        '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      },
      borderRadius: {
        none: '0',
        xs: '2px',
        sm: '4px',
        md: '6px',
        lg: '8px',
        xl: '12px',
        '2xl': '16px',
        '3xl': '24px',
        '4xl': '32px',
        full: '9999999px',
      },
      spacing: {
        '13': '3.25rem',
        '15': '3.75rem',
        '22': '5.5rem',
        '30': '7.5rem',
      },
      // Type scale (size / line-height) aligned to the Figma Design System typography.
      fontSize: {
        '2xs': ['0.5rem', '0.5rem'],
        xs: ['0.75rem', '1rem'],
        sm: ['0.875rem', '1.25rem'],
        base: ['1rem', '1.5rem'],
        lg: ['1.25rem', '1.75rem'],
        xl: ['1.375rem', '1.875rem'],
        '2xl': ['1.5rem', '2rem'],
        '3xl': ['2rem', '2.5rem'],
        '4xl': ['2.5rem', '3rem'],
        '5xl': ['3rem', '3.5rem'],
        '6xl': ['3.5rem', '4rem'],
      },
      fontFamily: {
        sans: ['var(--font-sans-serif)', 'system-ui', 'sans-serif'],
        serif: ['var(--font-serif)', 'Georgia', 'Cambria', '"Times New Roman"', 'Times', 'serif'],
      },
      animation: {
        // used inside briefing introduction screen like class names: animate-rightContentUpThenDown
        'chat-message-in': 'chat-message-in 300ms ease-out both',
        'reveal-down': 'reveal-down 700ms ease-out both',
        // Transform-only reveal: no opacity fade, so it is safe on an LCP element (the text is painted
        // immediately at full opacity, only sliding into place).
        'reveal-slide': 'reveal-slide 700ms ease-out both',
        'float-y-hero': 'float-y-hero 4000ms ease-in-out 4400ms both',
        'intro-image-height': 'intro-image-height 4000ms ease-in-out 4400ms both',
        'typing-box-sequence': 'typing-box-sequence 4000ms ease-in-out 1000ms both',
        'intro-brief-sequence': 'intro-brief-sequence 3400ms ease-in-out 4400ms both',
        'providers-card-in': 'providers-card-in 1000ms ease-out 8200ms both',
        'hide-typing-cursor': 'hide-typing-cursor 1ms linear 2318ms forwards',
        'remove-cursor-gap': 'remove-cursor-gap 1ms linear 2318ms forwards',
        'typing-reveal': 'typing-reveal 500ms steps(24,end) 2318ms forwards',
        'typing-reveal-2': 'typing-reveal 500ms steps(30,end) 3018ms forwards',
        sonar: 'sonar 5.4s cubic-bezier(0, 0, 0.2, 1) infinite',
        'sonar-pulse': 'sonar-pulse 1.6s ease-in-out infinite',
        'text-rotate': 'text-rotate 7.5s ease-in-out infinite',
        marquee: 'marquee 40s linear infinite',
      },
      keyframes: {
        marquee: {
          '0%': { transform: 'translateX(0)' },
          '100%': { transform: 'translateX(-50%)' },
        },
        sonar: {
          '0%': { transform: 'scale(0.9)', opacity: '0' },
          '12%': { opacity: '0.6' },
          '100%': { transform: 'scale(4.6)', opacity: '0' },
        },
        'sonar-pulse': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.03)' },
        },
        'text-rotate': {
          '0%': { opacity: '0', transform: 'translateY(120%)' },
          '6%': { opacity: '1', transform: 'translateY(0)' },
          '27%': { opacity: '1', transform: 'translateY(0)' },
          '33%': { opacity: '0', transform: 'translateY(-120%)' },
          '100%': { opacity: '0', transform: 'translateY(-120%)' },
        },
        'chat-message-in': {
          '0%': {
            opacity: '0',
            transform: 'translate3d(0, 8px, 0)',
          },
          '100%': {
            opacity: '1',
            transform: 'translate3d(0, 0, 0)',
          },
        },
        'reveal-down': {
          '0%': {
            opacity: '0',
            transform: 'translateY(-24px)',
          },
          '100%': {
            opacity: '1',
            transform: 'translateY(0)',
          },
        },
        'reveal-slide': {
          '0%': {
            transform: 'translateY(-24px)',
          },
          '100%': {
            transform: 'translateY(0)',
          },
        },
        'float-y-hero': {
          '0%': {
            transform: 'translate3d(0, 0, 0)',
          },
          '10%': {
            transform: 'translate3d(0, -80px, 0)',
          },
          '20%': {
            transform: 'translate3d(0, -80px, 0)',
          },
          '32%': {
            transform: 'translate3d(0, -80px, 0)',
          },
          '70%': {
            transform: 'translate3d(0, -80px, 0)',
          },
          '100%': {
            transform: 'translate3d(0, 0, 0)',
          },
        },
        'intro-image-height': {
          '0%': {
            height: '264px',
          },
          '10%': {
            height: '203px',
          },
          '70%': {
            height: '203px',
          },
          '100%': {
            height: '327px',
          },
        },
        'typing-box-sequence': {
          '0%': {
            opacity: '0',
            transform: 'translate3d(0, 24px, 0)',
          },
          '16%': {
            opacity: '1',
            transform: 'translate3d(0, -6px, 0)',
          },
          '22%': {
            opacity: '1',
            transform: 'translate3d(0, 2px, 0)',
          },
          '26%': {
            opacity: '1',
            transform: 'translate3d(0, 0, 0)',
          },
          '79.4%': {
            opacity: '1',
            transform: 'translate3d(0, 0, 0)',
          },
          '100%': {
            opacity: '0',
            transform: 'translate3d(0, -16px, 0)',
          },
        },
        'typing-reveal': {
          from: {
            maxWidth: '0',
          },
          to: {
            maxWidth: '100%',
          },
        },
        'remove-cursor-gap': {
          to: {
            paddingLeft: '0',
          },
        },
        'hide-typing-cursor': {
          to: {
            opacity: '0',
            width: '0',
            margin: '0',
          },
        },
        'intro-brief-sequence': {
          '0%': {
            opacity: '0',
            transform: 'translate3d(0, 24px, 0)',
          },
          '18%': {
            opacity: '1',
            transform: 'translate3d(0, -6px, 0)',
          },
          '26%': {
            opacity: '1',
            transform: 'translate3d(0, 2px, 0)',
          },
          '32%': {
            opacity: '1',
            transform: 'translate3d(0, 0, 0)',
          },
          '79.4%': {
            opacity: '1',
            transform: 'translate3d(0, 0, 0)',
          },
          '100%': {
            opacity: '0',
            transform: 'translate3d(0, -16px, 0)',
          },
        },
        'providers-card-in': {
          '0%': {
            opacity: '0',
            transform: 'translate3d(0, 74px, 0)',
          },
          '55%': {
            opacity: '1',
            transform: 'translate3d(0, -82px, 0)',
          },
          '75%': {
            opacity: '1',
            transform: 'translate3d(0, -68px, 0)',
          },
          '90%': {
            opacity: '1',
            transform: 'translate3d(0, -76px, 0)',
          },
          '100%': {
            opacity: '1',
            transform: 'translate3d(0, -74px, 0)',
          },
        },
      },
    },
  },
};

export default sortlistPreset;
